import { AuditStatus } from '@amzn/gbsa-helios-typescript-client';
import {
  daysUntilDate,
  formatDisplayDate,
  validateDateWithReason,
  DateReasonValidationError,
} from '../../../shared/domain';
import { AUDIT_ACTIONS } from '../tracking';

// Moved to shared/domain — re-exported here so existing audit call sites
// and tests keep working. Import from shared/domain in new code.
export { getResultBadgeType, isDateBeforeToday } from '../../../shared/domain';

const DAYS_UNTIL_URGENT = 3;

export interface AuditUrgencyStatus {
  isUrgent: boolean;
  isOverdue: boolean;
  daysUntilDue: number;
}

/**
 * Computes urgency/overdue status for an audit based on its expiration
 * date and current status.
 */
export function getAuditUrgencyStatus(
  expirationDate: Date | string | number | null | undefined,
  status: string,
): AuditUrgencyStatus {
  const daysUntilDue = daysUntilDate(expirationDate);
  // Statuses that must never render as "Urgent" or "Overdue" on the
  // due-date row:
  //   - COMPLETED / CANCELLED — terminal states, no action possible.
  //   - EXPIRED — the badge itself already conveys "past the deadline";
  //     showing an additional "Overdue" call-out on the due-date is
  //     redundant and inconsistent with desktop, which drops it.
  //   - SCHEDULED — created for a future date and not yet actionable.
  //     A scheduled audit whose expirationDate has slipped past today
  //     is not "overdue" in the user-facing sense — the user is not
  //     supposed to be acting on it yet.
  const nonUrgencyStatuses: string[] = [
    AuditStatus.COMPLETED,
    AuditStatus.CANCELLED,
    AuditStatus.EXPIRED,
    AuditStatus.SCHEDULED,
  ];
  const skipUrgency = nonUrgencyStatuses.includes(status);
  return {
    daysUntilDue,
    isUrgent: daysUntilDue <= DAYS_UNTIL_URGENT && daysUntilDue > 0 && !skipUrgency,
    isOverdue: daysUntilDue <= 0 && !skipUrgency,
  };
}

/**
 * Formats a date/timestamp for display (e.g. "Jul 9, 2026").
 * Returns '-' if the input is null/undefined/invalid.
 *
 * @deprecated Delegates to shared formatDisplayDate — import that directly
 * from shared/domain in new code. Kept for existing audit call sites.
 */
export function formatAuditDate(ts: Date | string | number | null | undefined): string {
  return formatDisplayDate(ts);
}

/**
 * Formats a score percentage for display (e.g. "92%").
 */
export function formatScorePercent(scorePercent: number): string {
  return `${Math.round(scorePercent * 100) / 100}%`;
}

/**
 * Filters questions by their main response result.
 * Returns all questions if filter is null.
 */
export function filterQuestionsByResult<
  T extends {
    responseDetails?:
      | { mainQuestionResponse?: { responseResult?: string | undefined } | undefined }
      | undefined;
  },
>(questions: T[], filter: string | null): T[] {
  if (!filter) return questions;
  return questions.filter(
    (q) => q.responseDetails?.mainQuestionResponse?.responseResult === filter,
  );
}

// Moved to shared/domain (validateDateWithReason) — aliased here so
// existing audit call sites and tests keep working.
export const ReopenValidationError = DateReasonValidationError;
export type ReopenValidationError = DateReasonValidationError;

/**
 * Single source of truth mapping audit statuses to their primary action
 * button config. The PrimaryActionKey type and the lookup function are
 * both derived from this map — the label keys are declared exactly once.
 */
const AUDIT_PRIMARY_ACTIONS = {
  [AuditStatus.NOT_STARTED]: {
    labelKey: 'startButton',
    trackingAction: AUDIT_ACTIONS.startAudit,
  },
  [AuditStatus.IN_PROGRESS]: {
    labelKey: 'continueButton',
    trackingAction: AUDIT_ACTIONS.continueAudit,
  },
  [AuditStatus.COMPLETED]: {
    labelKey: 'viewAuditButton',
    trackingAction: AUDIT_ACTIONS.viewAudit,
  },
  [AuditStatus.EXPIRED]: {
    labelKey: 'viewAuditButton',
    trackingAction: AUDIT_ACTIONS.viewAudit,
  },
} as const;

/** useMessages label keys for audit primary actions, derived from the map above. */
export type PrimaryActionKey =
  (typeof AUDIT_PRIMARY_ACTIONS)[keyof typeof AUDIT_PRIMARY_ACTIONS]['labelKey'];

/**
 * Derives the primary action button config for a given audit status.
 * Returns null when no primary action applies (e.g. CANCELLED).
 * The labelKey maps to a useMessages key; trackingAction maps to AUDIT_ACTIONS constants.
 */
export function getAuditPrimaryAction(status: string): {
  labelKey: PrimaryActionKey;
  trackingAction: string;
} | null {
  if (status in AUDIT_PRIMARY_ACTIONS) {
    return AUDIT_PRIMARY_ACTIONS[status as keyof typeof AUDIT_PRIMARY_ACTIONS];
  }
  return null;
}

/**
 * Validates reopen audit form inputs.
 * Thin delegate over the shared date + reason validator.
 */
export function validateReopenInput(
  expirationDate: string,
  reason: string,
): ReopenValidationError {
  return validateDateWithReason(expirationDate, reason);
}

/**
 * Adds an assignee to the list (immutable).
 */
export function addAssignee(current: string[], alias: string): string[] {
  return [...current, alias];
}

/**
 * Removes an assignee from the list (immutable).
 */
export function removeAssignee(current: string[], alias: string): string[] {
  return current.filter((a) => a !== alias);
}

/**
 * Returns true when the audit's status permits adding or removing
 * assignees — NOT_STARTED and IN_PROGRESS only. Terminal statuses
 * (COMPLETED, CANCELLED, EXPIRED) are frozen for owner changes.
 *
 * Used to gate the "Assignees" button on the detail header and the
 * corresponding overflow-menu item on the perform page.
 */
export function canManageAssignees(status: string): boolean {
  return status === AuditStatus.NOT_STARTED || status === AuditStatus.IN_PROGRESS;
}

/**
 * Returns true when the audit hasn't started yet — either its
 * status is `NOT_STARTED` (the default post-creation state) or
 * `SCHEDULED` (created for a future date). In both cases the FIRST
 * recorded answer flips the audit to `IN_PROGRESS` so the audit
 * list card and the detail page reflect that work is under way.
 *
 * Called alongside every answer save on the perform page. Once
 * the audit is already `IN_PROGRESS` (or any terminal status),
 * this returns false and the extra mutation is skipped. Mirrors
 * desktop's `updateAuditToInprogress` guard in
 * `src/helper/performAuditHelpers.ts`.
 */
export function shouldStartAudit(status: string): boolean {
  return status === AuditStatus.NOT_STARTED || status === AuditStatus.SCHEDULED;
}
